function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useMemo, useState } from 'react';
import { Page, Button, TableList, Typography, CustomModal, PageEmptyState, TextButton, PageErrorState, Spinner, Searchbox, PageContentHeader, useOpenUnsavedChangesModal } from 'seagull';
import { Plus } from 'seagull/icons';
import { useGlobalContext, useStore, useStoreActions } from "../../../contexts";
import { useOnMount } from "../../../hooks/useOnMount";
import { useServiceTemplateApi } from "../../../hooks/useServiceTemplateApi";
import { selectors } from "../../../store/state-selectors";
import { modalSize } from "../../../infra/modals";
import ServiceBuilder from "../ServiceBuilder/ServiceBuilder";
import { columnDefs, headerItems, useNewServices } from "./ServicePicker.utils";
import ServicePickerEmptyIcon from "./ServicePickerEmptyIcon";
import ServicePickerRow from "./ServicePickerRow";
import styles from "./ServicePicker.module.scss";
import { serviceTemplateModel, serviceModel } from "../../../models";
import { defaultBillingDayOfWeek } from "../../../constants";
import { StartBillingOnEnum } from "../../../types/api.model";
import { handlePromise, numberAndUnit } from "../../../infra/utils";
import { BillingDayOfMonthModel } from "../../../models";
export default function ServicePicker(props) {
  const {
    agreementData,
    dismissModal,
    registerCanDismiss
  } = props;
  const globalContext = useGlobalContext();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const {
    logBI
  } = useStoreActions();
  const isMemberUser = useStore(selectors.user.isMemberUser);
  const serviceTemplateApi = useServiceTemplateApi();
  const {
    fetchServiceTemplates: fetchServiceTemplatesFromStore
  } = useStoreActions();
  const businessSettings = useStore(state => {
    var _state$userInfo;
    return (_state$userInfo = state.userInfo) === null || _state$userInfo === void 0 || (_state$userInfo = _state$userInfo.worksAt) === null || _state$userInfo === void 0 ? void 0 : _state$userInfo.settings;
  });
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState({});
  const {
    newServices,
    markServiceAsNew
  } = useNewServices();
  useOnMount(() => {
    fetchServiceTemplates();
  });
  registerCanDismiss(async () => numSelected === 0 || openUnsavedChangesModal({
    title: 'Leave this page?',
    content: 'The selected service(s) will not be added to the agreement',
    primaryButtonLabel: 'Leave page'
  }));
  const filteredServices = useMemo(() => {
    if (!(data !== null && data !== void 0 && data.services)) return [];
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return data.services;
    return data.services.filter(service => service.name.toLowerCase().includes(normalizedSearch));
  }, [data, search]);
  const numSelected = useMemo(() => {
    let num = 0;
    for (const key in selectedRows) {
      if (selectedRows[key]) {
        num += 1;
      }
    }
    return num;
  }, [selectedRows]);
  function onToggleAllSelection(selectAll) {
    setSelectedRows(filteredServices.reduce((acc, _ref) => {
      let {
        id
      } = _ref;
      return Object.assign(acc, {
        [id]: selectAll
      });
    }, {}));
  }
  async function fetchServiceTemplates() {
    const services = await fetchServiceTemplatesFromStore();
    if (services) {
      setData({
        services: services.active
      });
    } else {
      setError(true);
      setData(null);
    }
  }
  async function createOrEditService() {
    const serviceNameBlacklist = data === null || data === void 0 ? void 0 : data.services.map(s => s.name);
    let newServiceId = '';
    const _onSave = async body => {
      const loaderName = 'servicePicker';
      globalContext.startLoader(loaderName);
      try {
        const res = await serviceTemplateApi.create(body);
        newServiceId = res.id;
        await fetchServiceTemplates();
        return {
          success: true
        };
      } catch (err) {
        globalContext.showErrorToast('Failed to save service');
        return {
          success: false
        };
      } finally {
        globalContext.stopLoader(loaderName);
      }
    };
    await globalContext.showModal({
      transparentBackdrop: true,
      content: p => /*#__PURE__*/React.createElement(ServiceBuilder, _extends({}, p, {
        serviceNameBlacklist: serviceNameBlacklist,
        agreementData: agreementData,
        serviceOrigin: agreementData.isActiveAgreement ? 'agreement' : 'draft',
        onSave: async output => {
          const body = serviceTemplateModel.fromService(output);
          return await handlePromise(_onSave(body));
        }
      }))
    });
    if (newServiceId) {
      markServiceAsNew(newServiceId);
      // Automatically select the new/edited service.
      if (!selectedRows[newServiceId]) {
        setSelectedRows(_objectSpread(_objectSpread({}, selectedRows), {}, {
          [newServiceId]: true
        }));
      }
    }
  }
  function applyAddServices() {
    if (!data) {
      return;
    }
    const selectedServiceIds = Object.keys(selectedRows).filter(key => !!selectedRows[key]);
    const selectedServices = data.services.filter(s => selectedServiceIds.includes(s.id));
    const translated = selectedServices.map(s => serviceModel.fromServiceTemplate(s, {
      isBilledUpfront: serviceTemplateModel.isRepeatableManual(s.billing) ? false : (businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billUpfront) || false,
      skipBillingOnAcceptance: businessSettings !== null && businessSettings !== void 0 && businessSettings.billUpfront ? (businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.startBillingOn) === StartBillingOnEnum.SkipBillingOnAcceptance : true,
      billingDayOfWeek: (businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billingDayOfWeek) || defaultBillingDayOfWeek,
      billingDayOfMonth: BillingDayOfMonthModel.make(businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billingDayOfMonth),
      prorateFirstInvoice: businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.prorateFirstInvoice,
      isPaused: businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.automaticServicesPausedByDefault
    }));
    dismissModal(translated, {
      force: true
    });
  }
  return /*#__PURE__*/React.createElement(CustomModal, _extends({
    className: styles.modal,
    "data-testid": "service-picker",
    onClose: () => dismissModal()
  }, modalSize.large, {
    footer: /*#__PURE__*/React.createElement("div", {
      className: styles.footer
    }, !!numSelected && /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "service-picker-num-selected"
    }, numberAndUnit(numSelected, 'Service'), " selected"), /*#__PURE__*/React.createElement(Button, {
      "data-testid": "service-picker-apply-btn",
      disabled: !numSelected,
      onClick: applyAddServices
    }, "Add services"))
  }), /*#__PURE__*/React.createElement(TableList.Provider, {
    enableRowSelection: true,
    columnDefs: columnDefs,
    selectedRows: selectedRows,
    onSelectionChange: (selectedRows, changes) => {
      // We assume we can only select/deselect one service at a time.
      const changedKey = Object.keys(changes)[0];
      logBI(11100, {
        serviceId: changedKey,
        selected: changes[changedKey]
      });
      setSelectedRows(selectedRows);
    },
    numRows: filteredServices.length,
    numSelectedRows: numSelected,
    onToggleAllSelection: onToggleAllSelection
  }, error ? /*#__PURE__*/React.createElement(PageErrorState, {
    title: "Select services",
    onRefresh: fetchServiceTemplates,
    skin: "secondary"
  }) : /*#__PURE__*/React.createElement(Page, {
    height: "100%",
    minWidth: "880px",
    skin: "secondary",
    header: /*#__PURE__*/React.createElement("div", {
      "data-testid": "uneditable-service-picker-header"
    }, "Select services"),
    subheader: /*#__PURE__*/React.createElement("div", {
      "data-testid": "uneditable-service-picker-description"
    }, "Search and select services from your Services page.", /*#__PURE__*/React.createElement("br", null), "If you don't see the one you want, you can create a new one."),
    actions: data && !isMemberUser && /*#__PURE__*/React.createElement(Button, {
      prefixIcon: /*#__PURE__*/React.createElement(Plus, null),
      "data-testid": "service-picker-new-btn",
      onClick: () => createOrEditService()
    }, "New service")
  }, data ? data.services.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Searchbox, {
    registerHotkey: true,
    value: search,
    onChange: event => setSearch(event.target.value),
    onClear: () => setSearch(''),
    "data-testid": "service-picker-search"
  })), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems,
    "data-testid": "service-picker-table-header"
  })), filteredServices.map((service, idx) => /*#__PURE__*/React.createElement(ServicePickerRow, {
    key: idx,
    service: service,
    isNew: newServices[service.id],
    onSelectionChange: () => {
      const changedKey = serviceModel.id(service);
      logBI(11100, {
        serviceId: changedKey,
        selected: service
      });
      setSelectedRows(_objectSpread(_objectSpread({}, selectedRows), {}, {
        [changedKey]: !selectedRows[changedKey]
      }));
    }
  }))) : /*#__PURE__*/React.createElement(PageEmptyState, {
    illustration: /*#__PURE__*/React.createElement(ServicePickerEmptyIcon, null),
    message: "Your services will be listed here",
    actions: /*#__PURE__*/React.createElement(TextButton, {
      "data-testid": "service-picker-empty-btn",
      onClick: () => createOrEditService()
    }, "Create a new service")
  }) : /*#__PURE__*/React.createElement("div", {
    className: styles.loading
  }, /*#__PURE__*/React.createElement(Spinner, {
    size: 40,
    "data-testid": "service-picker-loading"
  })))));
}